import type { TFunction } from 'i18next'

import type documentation from '@/locales/en/documentation.json'
import { isArchiveError, type ArchiveErrorCode } from './errors'
import { gapParts, nearestTime, parseTimeQuery, type GapKind } from './features'
import type { LoadResult } from './frameLoader'

type MessageKey = keyof typeof documentation

export type Frame = { image: HTMLCanvasElement | null; message: string | null }

type FrameBuilders = {
  [Kind in LoadResult['kind']]: (result: Extract<LoadResult, { kind: Kind }>) => Frame | null
}

const ARCHIVE_ERROR_MESSAGES: Record<ArchiveErrorCode, { key: MessageKey; named?: true }> = {
  'not-archive': { key: 'notArchive', named: true },
  nested: { key: 'nested', named: true },
  'no-photos': { key: 'noPhotos', named: true },
  'header-too-large': { key: 'headerTooLarge' },
  'geojson-too-large': { key: 'geojsonTooLarge' },
  'photo-too-large': { key: 'photoTooLarge' },
  compressed: { key: 'compressed' },
  empty: { key: 'emptyArchive' },
  unsupported: { key: 'unsupported' },
  'no-geojson': { key: 'noGeojson' },
  'bad-geojson': { key: 'badGeojson' },
  'no-features': { key: 'noFeatures' },
  'out-of-range': { key: 'outOfRange' },
  'photo-failed': { key: 'photoFailed' },
  encrypted: { key: 'encrypted' },
  'worker-failed': { key: 'workerFailed' },
}

const GAP_MESSAGES: Record<GapKind, MessageKey> = {
  exact: 'exact',
  seconds: 'secondsOff',
  minutes: 'minutesOff',
  hours: 'hoursOff',
}

export const archiveErrorMessage = (
  t: TFunction<'documentation'>,
  error: unknown,
  fallback: 'photoFailed' | 'openFailed' = 'photoFailed'
): string => {
  if (!isArchiveError(error)) return t(fallback)
  const message = ARCHIVE_ERROR_MESSAGES[error.code] ?? ARCHIVE_ERROR_MESSAGES.unsupported
  return message.named ? t(message.key, { name: error.detail ?? '' }) : t(message.key)
}

export const frameFromResult = (t: TFunction<'documentation'>, result: LoadResult): Frame | null => {
  const builders: FrameBuilders = {
    skipped: () => null,
    image: ({ image }) => ({ image, message: null }),
    missing: ({ path }) => ({ image: null, message: t('photoMissing', { path }) }),
    'no-path': () => ({ image: null, message: t('noImagePath') }),
    error: ({ error }) => ({ image: null, message: archiveErrorMessage(t, error) }),
  }
  return (builders[result.kind] as (value: LoadResult) => Frame | null)(result)
}

export type TimeJump = { hint: string; miss: boolean; position: number | null }

export const timeJump = (
  t: TFunction<'documentation'>,
  text: string,
  reference: Date,
  times: Array<number | null>
): TimeJump => {
  if (!text.trim()) return { hint: '', miss: false, position: null }
  const target = parseTimeQuery(text, reference)
  if (target == null) return { hint: t('unreadableTime'), miss: true, position: null }
  const found = nearestTime(times, target)
  if (!found) return { hint: t('noTimestamps'), miss: true, position: null }
  return { hint: gapMessage(t, gapParts(found.gap)), miss: false, position: found.index }
}

export const gapMessage = (t: TFunction<'documentation'>, gap: { kind: GapKind; value: number }): string =>
  t(GAP_MESSAGES[gap.kind], { count: gap.value })
